import { brandModelLinkedFields, mainBodyStoreUnionMultiSelect } from "../../components/Business/QueryFieldPresets";
import type { ForgeQuery } from '../../components/ForgeQueryBuilder';
import { FieldConfig } from "../../components/ForgeQueryBuilder";
import { useBrands, useMainBodyMap, useShopMap, useSkus, useStaffMap } from "../../services/store/organization.store";
import type { StaffItem } from "../../types/entity/staff";
import { AuditStatusMap } from "../../types/process/order-audit";
import type { MergedOrder } from "../../types/process/order";
import { OrderStatusMap, OrderType, OrderTabScene } from "../../types/process/order";
import { ORDER_STATUS_LABEL_MAP } from "../../types/process/order-data";
import { pickDurationRange } from "../../types/process/order-audit";
import { buildNumericMapOptions } from "../../utils/format.utils";
import dayjs from 'dayjs';

const PreHandScenes = [OrderTabScene.GroupAuditWait, OrderTabScene.GroupPre];
const ShowOrderTypeScenes = [OrderTabScene.GroupAll, OrderTabScene.GroupNonDevice];

export const getNonDeviceOrderField = (): FieldConfig => {
  return {
    key: 'order_type',
    label: '订单类型',
    type: 'cascader' as const,
    operators: ['in'],
    width: 150,
    cascaderProps: {
      multiple: true,
      maxTagCount: 'responsive',
      showCheckedStrategy: 'SHOW_CHILD',
    },
    getCascaderOptions: () => [
      { label: '常规订单', value: OrderType.Normal },
      { label: '续租订单', value: OrderType.Renewal },
      {
        label: '非设备订单',
        value: 'non_device',
        children: [
          { label: '赔偿金', value: OrderType.Compensation },
          { label: '补押金', value: OrderType.DepositSupplement },
          { label: '补租金', value: OrderType.RentSupplement },
          { label: '运费', value: OrderType.Freight },
          { label: '购买订单', value: OrderType.Buy },
          { label: '其他非设备订单', value: OrderType.OtherNonDevice },
        ],
      },
    ],
  };
};

export const getDefaultQuery = (scene?: OrderTabScene | string): ForgeQuery => {
  if (scene && PreHandScenes.includes(scene as OrderTabScene)) {
    const today = dayjs().format('YYYY-MM-DD');
    return {
      filter: {
        order_created_at: { operator: 'between', value: [today, today] },
        risk_type: { operator: 'eq', value: '盲发' },
      },
    };
  }
  return {};
};

export const useDefaultQueryFields = (scene?: OrderTabScene | string): FieldConfig[] => {
  const { brandList: brands } = useBrands();
  const { skuList: skus } = useSkus();
  const { shopList } = useShopMap();
  const { mainBodyList } = useMainBodyMap();
  const { staffMap } = useStaffMap();
  const orderStatusOptions = buildNumericMapOptions(OrderStatusMap, { excludedValues: [91] });
  const rrzuStatusOptions = buildNumericMapOptions(ORDER_STATUS_LABEL_MAP);
  const auditStatusOptions = buildNumericMapOptions(AuditStatusMap);

  const showOrderType = scene && ShowOrderTypeScenes.includes(scene as OrderTabScene);
  const shouldRememberRiskType = scene && PreHandScenes.includes(scene as OrderTabScene);

  const fieldMap: Record<string, FieldConfig> = {
    order_created_at: { key: 'order_created_at', label: '进件时间', type: 'date' as const, operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开始日期', '结束日期'] as [string, string] },
    store_id: {
      ...mainBodyStoreUnionMultiSelect(mainBodyList, shopList, 'store_id'),
      rememberKey: '/order',
    },
    order_no: { key: 'order_no', label: '订单号', type: 'string' as const, operators: ['eq'] },
    user_phone: { key: 'user_phone', label: '手机号', type: 'string' as const, operators: ['eq'] },
    user_name: { key: 'user_name', label: '姓名', type: 'string' as const },
    order_status: { key: 'order_status', label: '工单阶段', type: 'select' as const, operators: ['eq'], width: 150, options: orderStatusOptions },
    audit_status: {
      key: 'audit_status',
      label: '初审状态',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 160,
      options: auditStatusOptions,
    },
    rrzu_status: { key: 'rrzu_status', label: '订单状态', type: 'multiSelect' as const, operators: ['in'], width: 220, options: rrzuStatusOptions },
    warehouse_source: {
      key: 'warehouse_source',
      label: '采购渠道',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 180,
      options: [
        { label: '平台采购', value: 'platform_purchase' },
        { label: '自采', value: 'self_purchase' },
        { label: '第三方采购', value: 'third_party_purchase' },
        { label: '未知', value: 'unknown' },
      ],
    },
    aftersale_status: {
      key: 'aftersale_status',
      label: '售后状态',
      type: 'select' as const,
      operators: ['eq'],
      width: 140,
      options: [
        { label: '已退款', value: '已退款', queryValue: { key: 'aftersale_status', operator: 'eq', value: '已退款' } },
        { label: '未退款', value: '未退款', queryValue: { key: 'aftersale_status', operator: 'ne', value: '已退款' } },
      ],
    },
    buyout_price_status: {
      key: 'buyout_price_status',
      label: '最后期买断价',
      type: 'select' as const,
      width: 140,
      options: [
        { label: '有买断价', value: 'HAS', queryValue: { key: 'buyout_price_status', operator: 'eq', value: 'HAS' } },
        { label: '无买断价', value: 'NONE', queryValue: { key: 'buyout_price_status', operator: 'eq', value: 'NONE' } },
      ],
    },
    product_title: { key: 'product_title', label: '商品标题', type: 'string' as const },
    order_type: getNonDeviceOrderField(),

    risk_type: {
      key: 'risk_type',
      label: '风控类型',
      type: 'select' as const,
      width: 150,
      rememberKey: undefined,
      options: [
        { label: '盲发', value: '盲发', queryValue: { key: 'blind_type', operator: 'ne', value: '' } },
        { label: '通过', value: 'PASS' },
        { label: '高风险', value: 'HIGH_RISK' },
        { label: '预收', value: 'PRE_PAY' },
      ],
    },
    product_sku: brandModelLinkedFields(brands, skus)[0],
    product_quality: {
      key: 'product_quality',
      label: '成色',
      type: 'select' as const,
      options: [
        { label: '全新', value: 'NEW' },
        { label: '二手', value: 'SECOND_HAND' },
      ],
    },
    has_receipt_evidence: {
      key: 'has_receipt_evidence',
      label: '签收证据',
      type: 'select' as const,
      width: 140,
      options: [
        { label: '有证据', value: '1', queryValue: { key: 'receipt_evidence', operator: 'ne', value: "" } },
        { label: '无证据', value: '0', queryValue: { key: 'receipt_evidence', operator: 'eq', value: "" } },
      ],
    },
    decision_result: {
      key: 'decision_status',
      label: '决策结果',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 150,
      options: [
        { label: '放单', value: 3 },
        { label: '取消订单', value: 4 },
      ],
    },
    decision_operator_id: {
      key: 'decision_operator_id',
      label: '决策人',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 150,
      options: Array.from(staffMap.values()).map(staff => ({
        label: staff.name || staff.username || String(staff.id),
        value: staff.id,
      })),
    },
    logistics_no: { key: 'logistics_no', label: '物流单号', type: 'string' as const, operators: ['eq'] },
  };

  const commonFields = [
    'order_created_at', 'store_id', 'order_no', 'user_phone', 'user_name',
    'order_status', 'rrzu_status', 'warehouse_source', 'aftersale_status',
    'buyout_price_status', 'product_title', 'risk_type', 'product_sku', 'product_quality',
    'audit_status', 'decision_result', 'decision_operator_id'
  ];

  const withOrderTypeFields = [
    'order_created_at', 'store_id', 'order_no', 'user_phone', 'user_name',
    'order_status', 'rrzu_status', 'warehouse_source', 'aftersale_status',
    'product_title', 'order_type', 'risk_type', 'product_sku', 'product_quality',
    'audit_status', 'decision_result', 'decision_operator_id'
  ];

  const receiptFields = [
    'order_created_at', 'store_id', 'order_no', 'logistics_no', 'user_phone', 'user_name',
    'rrzu_status', 'warehouse_source', 'product_title', 'product_sku', 'product_quality', 'has_receipt_evidence'
  ];

  const sceneFieldsMap: Record<string, string[]> = {
    [OrderTabScene.GroupAll]: withOrderTypeFields,
    [OrderTabScene.GroupNonDevice]: withOrderTypeFields,
    [OrderTabScene.GroupReceiptManagement]: receiptFields,
    default: commonFields,
  };

  const fieldKeys = sceneFieldsMap[scene as string] || sceneFieldsMap.default;

  return fieldKeys.map(key => {
    const field = fieldMap[key];
    if (!field) return null;
    if (key === 'risk_type' && shouldRememberRiskType) {
      return { ...field, rememberKey: '/order/risk_type' };
    }
    return field;
  }).filter(Boolean) as FieldConfig[];
}

export const getOrderAuditDisplayName = (order: MergedOrder, staffMap: Map<number, StaffItem>) => {
  const ids = pickDurationRange(order.audit?.durationRange, 'name', 'all') as number[];
  const names = (ids || [])
    .map((id) => staffMap.get(id)?.name || staffMap.get(id)?.username || String(id))
    .filter((v) => v && v !== 'undefined');
  const fallbackId = order.order.auditId;
  const fallbackName = fallbackId
    ? (staffMap.get(fallbackId)?.name || staffMap.get(fallbackId)?.username || String(fallbackId))
    : '-';
  return names.length > 0 ? names.join(', ') : fallbackName;
};
